/**
 * The seam between "a request was refused until the person proves something"
 * and "ask the person".
 *
 * ── WHY A BROKER AND NOT A DIRECT CALL ──────────────────────────────────────
 *
 * The refusal arrives inside an axios interceptor, which is module scope with
 * no React tree beneath it. The dialog that answers it is a component. A broker
 * is the one-way channel between them: a provider REGISTERS a handler, the
 * interceptor REQUESTS a step-up and awaits the answer.
 *
 * Doing it the other way — having every caller catch the refusal code — was
 * rejected because the gates protect whole route families reached from dozens
 * of call sites. One missed call site is a dead end the operator cannot escape,
 * and it fails silently: the request 401s, the page shows an error, and nothing
 * suggests a prompt was even possible.
 *
 * Two brokers are built from this: the second factor (`mfaStepUpBroker`) and
 * recent sign-in (`recentAuthBroker`). They are separate instances because a
 * route can refuse for either reason, and each needs its own dialog.
 *
 * ── COALESCING IS LOAD-BEARING ──────────────────────────────────────────────
 *
 * A page fires several requests at once and they are ALL refused. Without
 * coalescing that is one dialog per request, stacked. For a second factor that
 * is fatal (a TOTP code is single-use, so the second prompt REFUSES the code
 * just typed); for a re-sign-in it is a password typed three times. One
 * in-flight promise, shared by every waiter, makes the common case survivable.
 * The FIRST asker's argument opens the prompt; later askers share its answer.
 */
import { createClientLogger } from '../utils/logger';

export interface StepUpBroker<TArgs extends unknown[]> {
  /**
   * Register the UI that can run the prompt. Returns an unregister function.
   * If nothing registers, `request` resolves FALSE rather than hanging, and the
   * original 401 surfaces normally: a pending promise nobody can settle is a
   * spinner that never stops.
   */
  register(fn: (...args: TArgs) => Promise<boolean>): () => void;
  /** True when a surface can actually run the prompt. */
  canPrompt(): boolean;
  /**
   * Ask, coalescing concurrent asks into one prompt. Never throws: a prompt
   * that fails is `false`, and the caller then surfaces its original error.
   * Turning a refusal into a *different* exception would hide why the request
   * was refused in the first place.
   */
  request(...args: TArgs): Promise<boolean>;
  /** Test seam. Never call from application code. */
  reset(): void;
}

export function createStepUpBroker<TArgs extends unknown[]>(name: string): StepUpBroker<TArgs> {
  const logger = createClientLogger(name);
  let handler: ((...args: TArgs) => Promise<boolean>) | null = null;
  let inFlight: Promise<boolean> | null = null;

  return {
    register(fn) {
      if (handler) {
        // Two providers mounted at once means two dialogs racing one answer.
        // Say so loudly rather than silently letting the last mount win.
        logger.warn('A step-up handler was already registered; replacing it');
      }
      handler = fn;
      return () => {
        if (handler === fn) handler = null;
      };
    },
    canPrompt() {
      return handler !== null;
    },
    request(...args) {
      if (!handler) return Promise.resolve(false);
      if (inFlight) return inFlight;

      inFlight = handler(...args)
        .catch((err) => {
          logger.warn('Step-up handler threw; treating as declined', { error: err });
          return false;
        })
        .finally(() => {
          inFlight = null;
        });

      return inFlight;
    },
    reset() {
      handler = null;
      inFlight = null;
    },
  };
}
